import { useState, useRef, useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { X, Upload, Loader2, Image as ImageIcon, Save } from "lucide-react";
import { ArticuloConfig, articulosConfigApi, uploadArticuloFoto, api } from "../../lib/api";
import { useAuth } from "../../lib/auth";

interface CategoriaOption { id: number; nombre: string; visibleMenu: boolean; visibleDelivery: boolean; activo: boolean; }

export function EditarArticuloModal({
  articulo, open, onClose,
}: {
  articulo: ArticuloConfig | null;
  open: boolean;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const user = useAuth((s) => s.user);
  const fileRef = useRef<HTMLInputElement>(null);

  const [fotoPreview, setFotoPreview] = useState<string | null>(null);
  const [fotoFile, setFotoFile] = useState<File | null>(null);
  const [categoriaId, setCategoriaId] = useState<number | "">("");
  const [descripcionDelivery, setDescripcionDelivery] = useState(articulo?.descripcionDelivery ?? "");
  const [precioDeliveryUsd, setPrecioDeliveryUsd] = useState(articulo?.precioDeliveryUsd ?? "");
  const [tiempoPreparacionMin, setTiempoPreparacionMin] = useState(articulo?.tiempoPreparacionMin?.toString() ?? "");
  const [error, setError] = useState<string | null>(null);

  // Cargar categorías disponibles
  const { data: categorias = [] } = useQuery<CategoriaOption[]>({
    queryKey: ["categorias"],
    queryFn: () => api<CategoriaOption[]>("/v1/categorias"),
    enabled: open,
  });

  // Reset cuando cambia el artículo
  useEffect(() => {
    if (articulo) {
      setCategoriaId((articulo as any).categoriaId ?? "");
      setDescripcionDelivery(articulo.descripcionDelivery ?? "");
      setPrecioDeliveryUsd(articulo.precioDeliveryUsd ?? "");
      setTiempoPreparacionMin(articulo.tiempoPreparacionMin?.toString() ?? "");
      setFotoPreview(articulo.fotoUrl || articulo.fotoIcgUrl || null);
      setFotoFile(null);
      setError(null);
    }
  }, [articulo]);

  const saveMut = useMutation({
    mutationFn: async () => {
      if (!articulo) return;
      // 1) Si hay foto nueva, subirla
      let fotoUrl: string | null | undefined = articulo.fotoUrl;
      if (fotoFile) {
        const orgId = user?.organizacionId ?? 1;
        const up = await uploadArticuloFoto(fotoFile, orgId, articulo.articuloId);
        fotoUrl = up.url;
      }
      // 2) Actualizar config con todos los cambios incluida fotoUrl
      await articulosConfigApi.actualizar(articulo.articuloId, {
        categoriaId: categoriaId === "" ? null : Number(categoriaId),
        descripcionDelivery: descripcionDelivery || null,
        precioDeliveryUsd: precioDeliveryUsd ? Number(precioDeliveryUsd) : null,
        tiempoPreparacionMin: tiempoPreparacionMin ? Number(tiempoPreparacionMin) : null,
        ...(fotoFile && fotoUrl ? { fotoUrl } : {}),
      });
      return { fotoUrl };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["articulos-config"] });
      onClose();
    },
    onError: (e: any) => setError(e.message || "Error guardando"),
  });

  function onFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setError("Solo imágenes");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError("Máximo 5MB");
      return;
    }
    setError(null);
    setFotoFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setFotoPreview(ev.target?.result as string);
    reader.readAsDataURL(file);
  }

  if (!open || !articulo) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 animate-fade-in"
      onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-ink-200">
          <div>
            <h2 className="font-display font-bold text-ink-800">{articulo.nombre}</h2>
            <p className="text-xs text-ink-500 font-mono">#{articulo.codArticulo} · {articulo.seccionNombre}</p>
          </div>
          <button onClick={onClose} className="text-ink-400 hover:text-ink-700">
            <X className="size-5" />
          </button>
        </div>

        {/* Contenido */}
        <div className="p-5 overflow-y-auto flex-1 space-y-4">
          {error && (
            <div className="px-3 py-2 bg-red-50 border border-red-200 rounded text-sm text-red-700">
              {error}
            </div>
          )}

          {/* Foto */}
          <div>
            <label className="block text-xs font-medium text-ink-600 mb-2">Foto del artículo</label>
            <div className="flex items-start gap-4">
              <div className="size-32 rounded-lg bg-ink-100 border-2 border-dashed border-ink-300 flex items-center justify-center overflow-hidden shrink-0">
                {fotoPreview ? (
                  <img src={fotoPreview} alt={articulo.nombre} className="size-full object-cover" />
                ) : (
                  <ImageIcon className="size-10 text-ink-400" />
                )}
              </div>
              <div className="flex-1">
                <input ref={fileRef} type="file" accept="image/*" onChange={onFileChange} className="hidden" />
                <button onClick={() => fileRef.current?.click()}
                  className="px-3 py-2 rounded-lg bg-brand-50 hover:bg-brand-100 text-brand-700 text-sm font-medium inline-flex items-center gap-2 transition-colors">
                  <Upload className="size-4" />
                  {fotoPreview ? "Cambiar foto" : "Subir foto"}
                </button>
                <p className="text-xs text-ink-500 mt-2">
                  JPG, PNG o WebP. Máximo 5MB.<br />
                  La foto se mostrará en el menú digital y delivery.
                </p>
              </div>
            </div>
          </div>

          <hr className="border-ink-200" />

          {/* Datos delivery */}
          <h3 className="text-sm font-bold text-ink-800">Configuración delivery</h3>

          <div>
            <label className="block text-xs font-medium text-ink-600 mb-1">Categoría</label>
            <select value={categoriaId}
              onChange={(e) => setCategoriaId(e.target.value === "" ? "" : Number(e.target.value))}
              className="input w-full">
              <option value="">— Sin categoría —</option>
              {categorias.filter(c => c.activo).map(c => (
                <option key={c.id} value={c.id}>
                  {c.nombre}
                  {!c.visibleMenu && !c.visibleDelivery ? " (oculta)" : ""}
                  {c.visibleMenu && !c.visibleDelivery ? " — solo menú" : ""}
                  {!c.visibleMenu && c.visibleDelivery ? " — solo delivery" : ""}
                </option>
              ))}
            </select>
            <p className="text-xs text-ink-500 mt-1">
              ¿No ves la categoría que querés? <a href="/tienda/categorias" target="_blank" className="text-indigo-600 hover:underline">Crear nueva</a>
            </p>
          </div>

          <div>
            <label className="block text-xs font-medium text-ink-600 mb-1">Descripción para delivery</label>
            <textarea value={descripcionDelivery}
              onChange={(e) => setDescripcionDelivery(e.target.value)}
              rows={3} placeholder="Descripción detallada para mostrar al cliente"
              className="input w-full" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-ink-600 mb-1">
                Precio delivery USD (opcional)
              </label>
              <input type="number" step="0.01" value={precioDeliveryUsd}
                onChange={(e) => setPrecioDeliveryUsd(e.target.value)}
                placeholder={`Default: ${articulo.precioUsd}`}
                className="input w-full" />
              <p className="text-xs text-ink-500 mt-1">Si difiere del precio del local</p>
            </div>
            <div>
              <label className="block text-xs font-medium text-ink-600 mb-1">Tiempo prep. (min)</label>
              <input type="number" value={tiempoPreparacionMin}
                onChange={(e) => setTiempoPreparacionMin(e.target.value)}
                placeholder="Ej: 15" className="input w-full" />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-ink-200 flex items-center justify-end gap-2 bg-ink-50">
          <button onClick={onClose} className="btn-secondary">Cancelar</button>
          <button onClick={() => saveMut.mutate()} disabled={saveMut.isPending}
            className="px-4 py-2 rounded-lg bg-brand-600 hover:bg-brand-700 text-white text-sm font-medium inline-flex items-center gap-2 disabled:opacity-50">
            {saveMut.isPending ? <Loader2 className="size-4 animate-spin" /> : <Save className="size-4" />}
            Guardar cambios
          </button>
        </div>
      </div>
    </div>
  );
}
